import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AppSidebar } from '@/components/AppSidebar';
import { PageLoadingOverlay, CONTENT_REGION_ID } from '@/components/PageLoadingOverlay';
import { DashboardOverview } from '@/pages/DashboardOverview';
import { Dashboard } from '@/pages/Dashboard';
import { Resources } from '@/pages/Resources';
import { Projects } from '@/pages/Projects';
import { Settings } from '@/pages/Settings';
import { UserSetup } from '@/pages/UserSetup';
import { UnallocatedBookings } from '@/pages/UnallocatedBookings';
import { OpenDemandRequests } from '@/pages/OpenDemandRequests';
import { UtilisationReport } from '@/pages/reports/UtilisationReport';
import { UtilisationForecast } from '@/pages/reports/UtilisationForecast';
import { AvailableCapacity, type AvailableCapacityReturnContext } from '@/pages/reports/AvailableCapacity';
import { type ProjectStaffingNavigateToCapacityContext } from '@/pages/reports/ProjectStaffing';
import { type PlanPreviewScenario } from '@/types/resourceRequest';

import { ResourceAllocation } from '@/pages/reports/ResourceAllocation';
import { ProjectStaffing } from '@/pages/reports/ProjectStaffing';
import { BenchReport } from '@/pages/reports/BenchReport';
import { RolesPermissionsPage } from '@/components/settings/RolesPermissionsPage';
import { EditRolePage } from '@/pages/EditRolePage';
import { SchedulerProvider } from '@/contexts/SchedulerContext';
import { FilterProvider } from '@/contexts/FilterContext';
import { AuditProvider } from '@/contexts/AuditContext';
import { AuditLogReport } from '@/pages/reports/AuditLogReport';
import { BookingDatabaseProvider } from '@/contexts/BookingDatabaseContext';
import { UserProvider } from '@/contexts/UserContext';
import { SavedBookingTypesProvider } from '@/contexts/SavedBookingTypesContext';
import { FieldOrderingProvider } from '@/contexts/FieldOrderingContext';
import { ClipboardProvider } from '@/contexts/ClipboardContext';
import { DataModeProvider } from '@/contexts/DataModeContext';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { OnboardingProvider } from '@/contexts/OnboardingContext';
import { OnboardingGate } from '@/components/onboarding/OnboardingGate';
import { TourProvider, useTour } from '@/contexts/TourContext';
import { TourOverlay } from '@/components/tour/TourOverlay';
import { HelpDrawerProvider } from '@/contexts/HelpDrawerContext';
import { HelpDrawer } from '@/components/help/HelpDrawer';
import { HelpButton } from '@/components/help/HelpButton';
import { SidebarProvider, SidebarInset } from '@/components/blocks/evo-sidebar';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { AccessDenied } from '@/components/AccessDenied';
import { SchedulerAccessGate } from '@/components/SchedulerAccessGate';
import { WhatIfScenario, type PlannerNavContext } from '@/types';
import { WhatIfCommitResult } from '@/components/calendar/BurnoutRiskPanel';

type AppPage = 'overview' | 'calendar' | 'heatmap' | 'resources' | 'projects' | 'settings' | 'open-demand-requests' | 'open-demand-unallocated' | 'dependent-fields' | 'report-utilisation' | 'report-utilisation-forecast' | 'report-available-capacity' | 'report-resource-allocation' | 'report-project-staffing' | 'report-bench' | 'report-audit-log' | 'roles-permissions' | 'roles-permissions-edit' | 'user-setup';

/**
 * Inner shell — rendered inside TourProvider so it can access useTour().
 * Receives setCurrentPage from the outer App so tour navigation works.
 */
function AppShell({ setCurrentPage, children }: {
  setCurrentPage: (page: AppPage) => void;
  children: React.ReactNode;
}) {
  const { setNavigationCallback, setStepActionCallback } = useTour();
  // Track the previous step so we know when we're leaving specific steps
  const prevTourStepRef = useRef<string | null>(null);
  // Holds the pending timeout ID for the delayed booking-form open, so we can
  // cancel it if the user clicks Next before the 400 ms delay has elapsed.
  const bookingFormOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setNavigationCallback((page: string) => {
      setCurrentPage(page as AppPage);
    });
  }, [setNavigationCallback, setCurrentPage]);

  useEffect(() => {
    setStepActionCallback((stepId: string) => {
      const bookingFormSteps = ['booking-form', 'suggest-resource'];

      if (stepId === 'booking-form') {
        // Open the booking form sheet so the tour can spotlight it.
        // Store the timer so it can be cancelled if the user advances quickly.
        bookingFormOpenTimerRef.current = setTimeout(() => {
          bookingFormOpenTimerRef.current = null;
          // Dispatch with mock data so the form is pre-populated for the tour
          window.dispatchEvent(new CustomEvent('tour:open-booking-form', {
            detail: {
              projectId: 'pd1',
              projectName: 'Website Redesign',
              requiredRole: 'Senior Developer',
              status: 'confirmed',
              durationType: 'full-day',
              description: 'Website Redesign',
              // Start 1 week from today, 2-week duration
              startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
              endDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
            }
          }));
        }, 400);
      } else if (stepId === 'suggest-resource') {
        // Booking form is already open — open the suggest resource dialog too.
        // Delay enough for the preselectedDate/endDate sync effects to propagate
        // into startDate/endDate state before the recommendation engine runs.
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('tour:open-suggest-resource'));
        }, 700);
      } else if (
        prevTourStepRef.current !== null &&
        bookingFormSteps.includes(prevTourStepRef.current) &&
        !bookingFormSteps.includes(stepId)
      ) {
        // Cancel the pending open timer if the user clicked Next before it fired.
        if (bookingFormOpenTimerRef.current !== null) {
          clearTimeout(bookingFormOpenTimerRef.current);
          bookingFormOpenTimerRef.current = null;
        }
        // Leaving the booking-form group — close suggest resource dialog then booking form.
        window.dispatchEvent(new CustomEvent('tour:close-suggest-resource'));
        window.dispatchEvent(new CustomEvent('tour:close-booking-form'));
      }

      if (stepId === 'conflict-resolution') {
        // Give the heatmap page a moment to render before opening the overlay
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('tour:open-whatif'));
        }, 700);
      } else if (prevTourStepRef.current === 'conflict-resolution') {
        // Only close the overlay when navigating AWAY from the conflict-resolution step
        window.dispatchEvent(new CustomEvent('tour:close-whatif'));
      }
      prevTourStepRef.current = stepId;
    });
  }, [setStepActionCallback]);

  return (
    <>
      {children}
      <TourOverlay />
    </>
  );
}

/**
 * Full-page transparent overlay shown only during the very first timeline load.
 * Covers the entire viewport (sidebar + content) to prevent user interactions
 * while still allowing the user to see the background UI.
 */
function TimelineLoadingOverlay({ currentPage }: { currentPage: AppPage }) {
  const { isLoading: isSchedulerLoading } = useScheduler();
  const { isLoading: isCompanyLoading } = useCompany();
  const { getScreenMode } = useDataMode();
  const hasCompletedFirstLoad = useRef(false);

  const isTimelinePage = currentPage === 'calendar' || currentPage === 'heatmap';
  const isLiveMode = getScreenMode('calendar') !== 'demo';
  const isLoading = isSchedulerLoading || isCompanyLoading;

  if (isTimelinePage && isLiveMode && !isLoading) {
    hasCompletedFirstLoad.current = true;
  }

  const showOverlay = isTimelinePage && isLiveMode && isLoading && !hasCompletedFirstLoad.current;

  if (!showOverlay) return null;

  return <PageLoadingOverlay message="Loading timeline..." />;
}

function PermissionGuardedPages({
  currentPage,
  setCurrentPage,
  editingRoleId,
  setEditingRoleId,
  settingsTab,
  pendingWhatIf,
  setPendingWhatIf,
  heatmapProjectFilter,
  setHeatmapProjectFilter,
  availableCapacityReturnContext,
  setAvailableCapacityReturnContext,
  plannerNavContext,
  setPlannerNavContext,
  handleNavigateToCalendarWithWhatIf,
  handleWhatIfComplete,
  handleWhatIfCommitted,
  committedWhatIf,
  clearCommittedWhatIf,
  pendingPlanPreview,
  setPendingPlanPreview,
  planPreviewReturnRequestId,
  setPlanPreviewReturnRequestId,
  onNavigateSettingsTab,
}: {
  currentPage: AppPage;
  setCurrentPage: (page: AppPage) => void;
  editingRoleId: number | null;
  setEditingRoleId: (id: number | null) => void;
  settingsTab: string;
  pendingWhatIf: WhatIfScenario | null;
  setPendingWhatIf: (s: WhatIfScenario | null) => void;
  heatmapProjectFilter: string | null;
  setHeatmapProjectFilter: (id: string | null) => void;
  availableCapacityReturnContext: AvailableCapacityReturnContext | null;
  setAvailableCapacityReturnContext: (ctx: AvailableCapacityReturnContext | null) => void;
  plannerNavContext: PlannerNavContext | null;
  setPlannerNavContext: (ctx: PlannerNavContext | null) => void;
  handleNavigateToCalendarWithWhatIf: (scenario: WhatIfScenario) => void;
  handleWhatIfComplete: (navigateBackToHeatmap?: boolean) => void;
  handleWhatIfCommitted: (result: WhatIfCommitResult) => void;
  committedWhatIf: WhatIfCommitResult | null;
  clearCommittedWhatIf: () => void;
  pendingPlanPreview: PlanPreviewScenario | null;
  setPendingPlanPreview: (p: PlanPreviewScenario | null) => void;
  planPreviewReturnRequestId: string | null;
  setPlanPreviewReturnRequestId: (id: string | null) => void;
  onNavigateSettingsTab: (tab: string) => void;
}) {
  const { canViewNavItem, canViewReport, canViewResourceDirectory, canViewRolesPermissions, canAccessSettings, canViewSettingsTab } = usePermissions();

  return (
    <>
      {currentPage === 'overview' && (canViewNavItem('viewDashboard') ? <DashboardOverview onNavigate={setCurrentPage} /> : <AccessDenied />)}
      {currentPage === 'calendar' && (
        <Dashboard
          whatIfScenario={pendingWhatIf}
          onWhatIfComplete={handleWhatIfComplete}
          onWhatIfCommitted={handleWhatIfCommitted}
          onNavigateToCalendarWithWhatIf={handleNavigateToCalendarWithWhatIf}
          plannerNavContext={plannerNavContext}
          onPlannerNavContextConsumed={() => setPlannerNavContext(null)}
          planPreviewScenario={pendingPlanPreview}
          onPlanPreviewClose={() => {
            if (pendingPlanPreview) {
              setPlanPreviewReturnRequestId(pendingPlanPreview.requestId);
              setCurrentPage('open-demand-requests');
            }
            setPendingPlanPreview(null);
          }}
          onNavigateToIntegrations={() => onNavigateSettingsTab('integrations')}
        />
      )}
      {currentPage === 'heatmap' && (canViewNavItem('viewHeatmap') ? (
        <Dashboard
          heatmapMode
          initialProjectFilter={heatmapProjectFilter}
          onNavigateToCalendarWithWhatIf={handleNavigateToCalendarWithWhatIf}
          initialResolvedCommit={committedWhatIf}
          onResolvedCommitConsumed={clearCommittedWhatIf}
          onNavigateToIntegrations={() => onNavigateSettingsTab('integrations')}
        />
      ) : <AccessDenied />)}
      {currentPage === 'resources' && (canViewResourceDirectory() ? <Resources /> : <AccessDenied />)}
      {currentPage === 'projects' && (canViewNavItem('viewProjects') ? <Projects /> : <AccessDenied />)}
      {currentPage === 'settings' && (canAccessSettings() ? <Settings initialTab={settingsTab} onNavigate={setCurrentPage} /> : <AccessDenied />)}
      {currentPage === 'user-setup' && (canViewSettingsTab('viewUserSetup') ? <UserSetup /> : <AccessDenied />)}
      {currentPage === 'open-demand-requests' && (canViewNavItem('viewOpenDemand') ? (
        <OpenDemandRequests
          onPreviewOnCalendar={(preview) => {
            setPendingPlanPreview(preview);
            setCurrentPage('calendar');
          }}
          initialOpenRequestId={planPreviewReturnRequestId ?? undefined}
          onInitialOpenRequestIdConsumed={() => setPlanPreviewReturnRequestId(null)}
        />
      ) : <AccessDenied />)}
      {currentPage === 'open-demand-unallocated' && (canViewNavItem('viewOpenDemand') ? (
        <UnallocatedBookings
          onNavigateToPlanner={(_bookingId?: string) => setCurrentPage('calendar')}
        />
      ) : <AccessDenied />)}
      {currentPage === 'report-utilisation' && (canViewReport('utilisationReport') ? <UtilisationReport onNavigateToHeatmap={() => setCurrentPage('heatmap')} /> : <AccessDenied />)}
      {currentPage === 'report-utilisation-forecast' && (canViewReport('utilisationForecastReport') ? <UtilisationForecast /> : <AccessDenied />)}
      {currentPage === 'report-available-capacity' && (canViewReport('availableCapacityReport') ? (
        <AvailableCapacity
          onNavigate={(page) => {
            setCurrentPage(page as AppPage);
            if (page !== 'report-available-capacity') {
              setAvailableCapacityReturnContext(null);
            }
          }}
          returnContext={availableCapacityReturnContext ?? undefined}
        />
      ) : <AccessDenied />)}
      {currentPage === 'report-resource-allocation' && (canViewReport('resourceAllocationReport') ? (
        <ResourceAllocation
          onNavigate={(page) => setCurrentPage(page as AppPage)}
          onNavigateToCalendarWithRange={(ctx) => {
            setPlannerNavContext(ctx);
            setCurrentPage('calendar');
          }}
        />
      ) : <AccessDenied />)}
      {currentPage === 'report-project-staffing' && (canViewReport('projectStaffingReport') ? (
        <ProjectStaffing
          onNavigate={(page) => setCurrentPage(page as AppPage)}
          onNavigateToHeatmapWithProject={(projectId) => setHeatmapProjectFilter(projectId)}
          onNavigateToAvailableCapacity={(ctx: ProjectStaffingNavigateToCapacityContext) => {
            setAvailableCapacityReturnContext({
              returnToPage: 'report-project-staffing',
              projectId: ctx.projectId,
              roleName: ctx.roleName,
            });
          }}
        />
      ) : <AccessDenied />)}
      {currentPage === 'report-bench' && (canViewReport('benchReport') ? <BenchReport /> : <AccessDenied />)}
      {currentPage === 'report-audit-log' && <AuditLogReport />}
      {currentPage === 'roles-permissions' && (canViewRolesPermissions() ? (
        <RolesPermissionsPage onNavigate={(page, roleId) => {
          if (page === 'roles-permissions-edit') {
            setEditingRoleId(roleId ?? null);
          }
          setCurrentPage(page);
        }} />
      ) : <AccessDenied />)}
      {currentPage === 'roles-permissions-edit' && (canViewRolesPermissions() ? (
        <EditRolePage
          roleId={editingRoleId}
          onNavigate={(page) => {
            if (page === 'roles-permissions') {
              setCurrentPage('roles-permissions' as AppPage);
            } else {
              setCurrentPage(page as AppPage);
            }
          }}
        />
      ) : <AccessDenied />)}
    </>
  );
}

export default function App({ defaultDataMode = 'demo', liveOnly }: { defaultDataMode?: 'demo' | 'live'; liveOnly?: boolean } = {}) {
  const [currentPage, setCurrentPage] = useState<AppPage>('calendar');
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [settingsTab, setSettingsTab] = useState<string>('settings');
  // What-if scenario: set by the heatmap suggestions panel, consumed by the calendar view
  const [pendingWhatIf, setPendingWhatIf] = useState<WhatIfScenario | null>(null);
  // Commit result lifted from the calendar view so the (separately mounted)
  // heatmap burnout panel can render the resolved success banner on return.
  const [committedWhatIf, setCommittedWhatIf] = useState<WhatIfCommitResult | null>(null);

  // Plan preview: set when user clicks "Preview on Calendar" in ResourceRequestPlanDrawer.
  const [pendingPlanPreview, setPendingPlanPreview] = useState<PlanPreviewScenario | null>(null);
  // Tracks which request to re-open when the user clicks "Back to plan" from preview mode
  const [planPreviewReturnRequestId, setPlanPreviewReturnRequestId] = useState<string | null>(null);

  // Project filter to pre-apply when navigating from Project Staffing → heatmap
  const [heatmapProjectFilter, setHeatmapProjectFilter] = useState<string | null>(null);

  // Context to pre-populate Available Capacity booking form (set when navigating from Project Staffing)
  const [availableCapacityReturnContext, setAvailableCapacityReturnContext] = useState<AvailableCapacityReturnContext | null>(null);

  // One-shot view + date range for the Planner (set when navigating from the Resource Allocation report)
  const [plannerNavContext, setPlannerNavContext] = useState<PlannerNavContext | null>(null);

  /** Called by BurnoutRiskPanelWrapper when Apply/Assign is pressed */
  const handleNavigateToCalendarWithWhatIf = useCallback((scenario: WhatIfScenario) => {
    setPendingWhatIf(scenario);
    setCurrentPage('calendar');
  }, []);

  /** Called by ResourceCalendar when the scenario is committed or discarded */
  const handleWhatIfComplete = useCallback((navigateBackToHeatmap?: boolean) => {
    setPendingWhatIf(null);
    if (navigateBackToHeatmap) {
      setCurrentPage('heatmap');
    }
  }, []);

  /** Called by the calendar-view ResourceCalendar when a rebalance is committed —
   *  seeds the heatmap burnout panel's resolved success banner on return. */
  const handleWhatIfCommitted = useCallback((result: WhatIfCommitResult) => {
    setCommittedWhatIf(result);
  }, []);
  const clearCommittedWhatIf = useCallback(() => setCommittedWhatIf(null), []);

  return (
    <BookingDatabaseProvider>
    <HelpDrawerProvider>
    <TourProvider>
    <UserProvider>
      <SchedulerAccessGate>
      <DataModeProvider defaultMode={defaultDataMode} liveOnly={liveOnly}>
        <AuditProvider>
        <CompanyProvider>
        <PermissionsProvider>
        <SchedulerProvider>
          <SavedBookingTypesProvider>
            <FieldOrderingProvider>
              <FilterProvider>
                <ClipboardProvider>
                  <OnboardingProvider>
                  <AppShell setCurrentPage={setCurrentPage}>
                  <SidebarProvider
                    defaultOpen={false}
                    style={{ "--sidebar-width": "16rem" } as React.CSSProperties}
                    className="fixed inset-0 overflow-hidden"
                  >
                    <AppSidebar
                      currentPage={currentPage}
                      onNavigate={(page) => setCurrentPage(page as typeof currentPage)}
                      settingsTab={settingsTab}
                      onNavigateSettings={(tab) => { setSettingsTab(tab); setCurrentPage('settings'); }}
                    />

                    <SidebarInset id={CONTENT_REGION_ID} className="h-screen overflow-hidden">
                      <PermissionGuardedPages
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        editingRoleId={editingRoleId}
                        setEditingRoleId={setEditingRoleId}
                        settingsTab={settingsTab}
                        pendingWhatIf={pendingWhatIf}
                        setPendingWhatIf={setPendingWhatIf}
                        heatmapProjectFilter={heatmapProjectFilter}
                        setHeatmapProjectFilter={setHeatmapProjectFilter}
                        availableCapacityReturnContext={availableCapacityReturnContext}
                        setAvailableCapacityReturnContext={setAvailableCapacityReturnContext}
                        plannerNavContext={plannerNavContext}
                        setPlannerNavContext={setPlannerNavContext}
                        handleNavigateToCalendarWithWhatIf={handleNavigateToCalendarWithWhatIf}
                        handleWhatIfComplete={handleWhatIfComplete}
                        handleWhatIfCommitted={handleWhatIfCommitted}
                        committedWhatIf={committedWhatIf}
                        clearCommittedWhatIf={clearCommittedWhatIf}
                        pendingPlanPreview={pendingPlanPreview}
                        setPendingPlanPreview={setPendingPlanPreview}
                        planPreviewReturnRequestId={planPreviewReturnRequestId}
                        setPlanPreviewReturnRequestId={setPlanPreviewReturnRequestId}
                        onNavigateSettingsTab={(tab) => { setSettingsTab(tab); setCurrentPage('settings'); }}
                      />
                    </SidebarInset>

                    {/* Onboarding wizard — renders via portal above all app content */}
                    <OnboardingGate
                      setCurrentPage={(page) => setCurrentPage(page as typeof currentPage)}
                      setSettingsTab={setSettingsTab}
                    />

                    {/* Help drawer — page-aware, renders via portal */}
                    <HelpDrawer
                      currentPage={currentPage}
                      onNavigate={(page) => setCurrentPage(page as typeof currentPage)}
                    />

                    {/* Content-area transparent overlay — blocks clicks on the page while timeline APIs load */}
                    <TimelineLoadingOverlay currentPage={currentPage} />
                  </SidebarProvider>

                  </AppShell>
                  </OnboardingProvider>
                </ClipboardProvider>
              </FilterProvider>
            </FieldOrderingProvider>
          </SavedBookingTypesProvider>
        </SchedulerProvider>
        </PermissionsProvider>
        </CompanyProvider>
        </AuditProvider>
      </DataModeProvider>
      </SchedulerAccessGate>
    </UserProvider>
    </TourProvider>
    </HelpDrawerProvider>
    </BookingDatabaseProvider>
  );
}
